import React from "react";
import { Link } from "react-router-dom";
import {
  Phone,
  MapPin,
  Clock3,
  Mail,
  Facebook,
  Instagram,
  Music2,
  Camera,
} from "lucide-react";
import { FOOTER } from "@/lib/testIds";
import { RESTAURANT, PLATFORMS, SOCIALS, HALAL } from "@/data/catalog";
import { useRestaurant } from "@/lib/brandingStore";
import { Logo } from "@/components/brand/Logo";

const navLinks = [
  { to: "/", label: "Accueil" },
  { to: "/menu", label: "Menu" },
  { to: "/offres", label: "Offres" },
  { to: "/contact", label: "Contact" },
];

const legalLinks = [
  { to: "/mentions-legales", label: "Mentions légales" },
  { to: "/allergenes", label: "Allergènes" },
];

const SOCIAL_ICONS = {
  facebook: Facebook,
  instagram: Instagram,
  tiktok: Music2,
  snapchat: Camera,
};

export const Footer = () => {
  const r = useRestaurant();
  const phoneTel = r.phone_tel || RESTAURANT.phoneTel;
  return (
    <footer
      data-testid={FOOTER.root}
      className="le11-noise mt-12 bg-[#0F0F0F] text-white border-t border-[#262626]"
    >
      <div className="mx-auto max-w-7xl px-4 sm:px-6 py-12 grid gap-10 lg:grid-cols-12">
        {/* Brand */}
        <div className="lg:col-span-3">
          <Logo size="lg" tone="light" tagline align="start" />
          <p className="mt-3 text-[10px] uppercase tracking-[0.28em] text-[#C9A875]/80 font-street">
            Fait maison · Viandes halal sélectionnées
          </p>
        </div>

        {/* Navigation */}
        <div className="lg:col-span-2">
          <div className="text-xs uppercase tracking-[0.22em] text-white/40 mb-3">
            Navigation
          </div>
          <ul className="space-y-2 text-sm">
            {navLinks.map((l) => (
              <li key={l.to}>
                <Link
                  to={l.to}
                  className="text-white/80 hover:text-[#F5EFE6] transition-colors"
                >
                  {l.label}
                </Link>
              </li>
            ))}
          </ul>
        </div>

        {/* Coordonnées */}
        <div className="lg:col-span-3">
          <div className="text-xs uppercase tracking-[0.22em] text-white/40 mb-3">
            Coordonnées
          </div>
          <a
            href={phoneTel}
            data-testid="footer-phone"
            className="inline-flex items-center gap-3 rounded-lg border border-[#262626] bg-[#171717] px-4 py-3 hover:border-[#FF9500]/50 transition-colors"
          >
            <span className="inline-flex h-9 w-9 items-center justify-center rounded-md bg-[#FF9500] text-black shrink-0">
              <Phone size={16} strokeWidth={2.6} />
            </span>
            <span className="font-display text-xl tracking-wider text-white">
              {r.phone_display}
            </span>
          </a>

          <div className="mt-4 flex items-start gap-2 text-xs text-white/70 leading-snug">
            <MapPin size={14} className="text-[#C9A875] shrink-0 mt-0.5" />
            <div>
              {r.address_street}
              <br />
              {r.address_zip} {r.address_city}
            </div>
          </div>

          <div className="mt-3 flex items-start gap-2 text-xs text-white/70 leading-snug">
            <Mail size={14} className="text-[#C9A875] shrink-0 mt-0.5" />
            <a
              href={`mailto:${r.email}`}
              data-testid="footer-email"
              className="hover:text-[#F5EFE6] transition-colors break-all"
            >
              {r.email}
            </a>
          </div>

          <div className="mt-3 flex items-start gap-2 text-xs text-white/70 leading-snug">
            <Clock3 size={14} className="text-[#C9A875] shrink-0 mt-0.5" />
            <ul className="space-y-0.5">
              {(r.hours || []).map((h) => (
                <li key={h.day}>
                  <span className="text-white/50">{h.day}</span> · {h.range || "—"}
                </li>
              ))}
            </ul>
          </div>

          {/* Réseaux sociaux — désactivés si pas de lien */}
          <div className="mt-5 text-xs uppercase tracking-[0.22em] text-white/40 mb-2">
            Suivez-nous
          </div>
          <div className="flex items-center gap-2">
            {SOCIALS.map((s) => {
              const Icon = SOCIAL_ICONS[s.icon] ?? Music2;
              const enabled = !!s.url;
              const cls =
                "inline-flex h-9 w-9 items-center justify-center rounded-md border transition-colors";
              return enabled ? (
                <a
                  key={s.id}
                  href={s.url}
                  target="_blank"
                  rel="noreferrer"
                  aria-label={s.name}
                  data-testid={`footer-social-${s.id}`}
                  className={`${cls} border-[#262626] bg-[#171717] text-white/70 hover:text-[#F5EFE6] hover:border-[#F5EFE6]/30`}
                >
                  <Icon size={16} />
                </a>
              ) : (
                <span
                  key={s.id}
                  aria-disabled="true"
                  aria-label={`${s.name} — non disponible`}
                  data-testid={`footer-social-${s.id}`}
                  title={`${s.name} — à connecter par le restaurant`}
                  className={`${cls} border-[#1F1F1F] bg-[#121212] text-white/25 cursor-not-allowed`}
                >
                  <Icon size={16} />
                </span>
              );
            })}
          </div>
        </div>

        {/* Plateformes */}
        <div className="lg:col-span-4">
          <div className="text-xs uppercase tracking-[0.22em] text-white/40 mb-3">
            Commandes sur plateformes
          </div>
          <div className="grid grid-cols-2 gap-3">
            {PLATFORMS.map((p) => {
              const active = p.status === "active" && p.url;
              const shared =
                "group flex items-center justify-between gap-2 rounded-lg border bg-[#171717] px-4 py-3 transition-colors";
              const inner = (
                <>
                  <span
                    className="font-street text-sm tracking-wider"
                    style={{ color: p.color }}
                  >
                    {p.name}
                  </span>
                  <span className="text-[10px] uppercase tracking-[0.22em] font-street text-white/50">
                    {active ? "Commander" : "Bientôt"}
                  </span>
                </>
              );
              return active ? (
                <a
                  key={p.id}
                  href={p.url}
                  target="_blank"
                  rel="noreferrer"
                  data-testid={`footer-platform-${p.id}`}
                  className={`${shared} border-[#262626] hover:border-white/30`}
                >
                  {inner}
                </a>
              ) : (
                <div
                  key={p.id}
                  aria-disabled="true"
                  data-testid={`footer-platform-${p.id}`}
                  className={`${shared} border-[#1F1F1F] opacity-80 cursor-not-allowed`}
                >
                  {inner}
                </div>
              );
            })}
          </div>
          <div className="mt-3 text-[11px] text-white/40">
            Livraison bientôt disponible via plateformes partenaires.
          </div>
        </div>
      </div>

      {/* Halal compact en pied */}
      <div className="border-t border-[#262626]">
        <div
          id="halal-footer"
          className="mx-auto max-w-7xl px-4 sm:px-6 py-5 flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4"
        >
          <div className="font-street text-[11px] uppercase tracking-[0.22em] text-[#C9A875]">
            {HALAL.title}
          </div>
          <div className="text-[11px] text-white/55 leading-snug max-w-xl">
            {HALAL.text}
          </div>
          <div className="flex flex-wrap gap-1.5 sm:ml-auto">
            {HALAL.organisations.map((o) => (
              <span
                key={o}
                className="font-street text-[10px] uppercase tracking-[0.22em] rounded-md border border-[#262626] bg-[#0A0A0A] px-2 py-0.5 text-white/70"
              >
                {o}
              </span>
            ))}
          </div>
        </div>
      </div>

      <div className="border-t border-[#262626]">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 py-4 flex flex-col sm:flex-row items-center justify-between gap-3 text-[11px] text-white/40">
          <span>
            © {new Date().getFullYear()} {r.name} · {r.domain} · Tous droits réservés.
          </span>
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
            {legalLinks.map((l) => (
              <Link
                key={l.to}
                to={l.to}
                data-testid={`footer-legal-${l.to.slice(1)}`}
                className="hover:text-[#F5EFE6] transition-colors"
              >
                {l.label}
              </Link>
            ))}
            <span className="font-street tracking-[0.22em] text-[#C9A875] uppercase">
              {r.slogan}
            </span>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
